import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Guards the lazily-loaded route tree.
 *
 * With route-level code splitting, a redeploy changes chunk hashes. A visitor
 * who kept an old tab open will 404 on the missing chunk when they navigate,
 * which would otherwise leave #root empty. We detect that dynamic-import
 * failure and do a single, cooldown-guarded reload to fetch the fresh chunks;
 * any other error shows a minimal recovery UI instead of a blank page.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    const isChunkError =
      /Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
        error?.message ?? ""
      );

    if (isChunkError) {
      const KEY = "chunk-reload-ts";
      const last = Number(sessionStorage.getItem(KEY) || 0);
      // Only auto-reload once per 10s window to avoid a reload loop.
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700 }}>Something went wrong</h1>
          <p style={{ color: "hsl(var(--muted-foreground))" }}>
            Please refresh the page to continue.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "0.6rem 1.25rem",
              borderRadius: "0.75rem",
              background: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              fontWeight: 600,
            }}
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
